import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import OverviewTab from './OverviewTab';
import QualityTab from './QualityTab';
import ProductivityTab from './ProductivityTab';
import './reports.css';

// Reports — localization analytics. Tabbed workspace (Overview / Quality /
// Productivity) driven entirely by /api/dashboard/reports, which returns the
// real workspace dataset shaped to match the design's window.RD object.

const TABS = [['overview', 'Overview'], ['quality', 'Quality'], ['productivity', 'Productivity']];
const TAB_IDS = TABS.map((x) => x[0]);

const I = {
    download: (<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2v7m0 0l-3-3m3 3l3-3M2.5 11.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    cal: (<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="3" width="10" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" /><path d="M2 5.5h10M4.5 1.5v2M9.5 1.5v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>),
    caret: (<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2.5 4l2.5 2.5L7.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    filter: (<svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M1.5 3h11M3.5 7h7M5.5 11h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>),
    check: (<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M3 7.5l2.5 2.5L11 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    x: (<svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>),
    mail: (<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="3" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" /><path d="M2 4l5 3.5L12 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    doc: (<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M4 2h5l3 3v9H4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M9 2v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>),
    sheet: (<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="2.5" y="2.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" /><path d="M2.5 6.5h11M2.5 10h11M6.5 2.5v11" stroke="currentColor" strokeWidth="1.3" /></svg>),
    img: (<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" /><circle cx="6" cy="6.5" r="1.2" stroke="currentColor" strokeWidth="1.2" /><path d="M3 12l3.5-3.5 2.5 2 2-1.5L13 11.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>),
};

function Pop({ open, onClose, children, align = 'left', width }) {
    if (!open) return null;
    return (
        <>
            <div className="rpt-pop-scrim" onMouseDown={onClose} />
            <div className={`rpt-pop ${align}`} style={width ? { width } : undefined} onMouseDown={(e) => e.stopPropagation()}>{children}</div>
        </>
    );
}

function Facet({ id, label, options, selected, onToggle, onClear, open, setOpen }) {
    const count = selected.length;
    return (
        <div className="rpt-facet-wrap">
            <button type="button" className={`rpt-facet ${count ? 'applied' : ''}`} onClick={() => setOpen(open === id ? null : id)}>
                <span className="k">{label}</span>
                {count > 0 && <span className="c">{count}</span>}
                <span className="caret">{I.caret}</span>
            </button>
            <Pop open={open === id} onClose={() => setOpen(null)} width={232}>
                <div className="rpt-pop-h"><b>{label}</b>{count > 0 && <button type="button" className="rpt-pop-clear" onClick={() => onClear(id)}>Clear</button>}</div>
                <div className="rpt-pop-list">
                    {options.length === 0 && <div className="rpt-pop-opt" style={{ color: 'var(--ink-4)' }}>No options</div>}
                    {options.map((opt) => {
                        const on = selected.includes(opt);
                        return (
                            <button type="button" key={opt} className={`rpt-pop-opt ${on ? 'on' : ''}`} onClick={() => onToggle(id, opt)}>
                                <span className="bx">{on && I.check}</span>{opt}
                            </button>
                        );
                    })}
                </div>
            </Pop>
        </div>
    );
}

function ScheduleForm({ tabLabel, onDone }) {
    const [freq, setFreq] = useState('Weekly');
    const [fmt, setFmt] = useState('PDF');
    const [to, setTo] = useState('');
    return (
        <>
            <div className="rpt-pop-h"><b>Schedule {tabLabel}</b></div>
            <div className="rpt-sched">
                <label className="rpt-field"><span>Frequency</span>
                    <div className="rpt-seg">{['Daily', 'Weekly', 'Monthly'].map((f) => <button type="button" key={f} className={freq === f ? 'on' : ''} onClick={() => setFreq(f)}>{f}</button>)}</div>
                </label>
                <label className="rpt-field"><span>Format</span>
                    <div className="rpt-seg">{['PDF', 'CSV'].map((f) => <button type="button" key={f} className={fmt === f ? 'on' : ''} onClick={() => setFmt(f)}>{f}</button>)}</div>
                </label>
                <label className="rpt-field"><span>Recipients</span>
                    <div className="rpt-input">{I.mail}<input value={to} onChange={(e) => setTo(e.target.value)} placeholder="email@company.com" /></div>
                </label>
                <button type="button" className="rpt-sched-go" onClick={() => onDone(`Scheduled ${freq.toLowerCase()} · ${fmt}${to ? ` → ${to}` : ''}`)}>{I.cal}<span>Schedule report</span></button>
            </div>
        </>
    );
}

const Reports = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [D, setD] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);

    const [tab, setTab] = useState(() => {
        try { const v = localStorage.getItem('rush.reports.tab'); return TAB_IDS.includes(v) ? v : 'overview'; } catch (e) { return 'overview'; }
    });
    const [range, setRange] = useState('month');
    const [menu, setMenu] = useState(null);
    const [filters, setFilters] = useState({ lang: [], project: [] });
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (!token) return undefined;
        let cancelled = false;
        setLoading(true);
        axios.get('/api/dashboard/reports', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (!cancelled) { setD(r.data); setLoading(false); } })
            .catch((err) => {
                if (cancelled) return;
                const status = err.response && err.response.status;
                if (status === 401) { dispatch(logout()); navigate('/login'); } else if (status === 403) { setDenied(true); setLoading(false); } else { setError('Failed to load reports.'); setLoading(false); }
            });
        return () => { cancelled = true; };
    }, [token, dispatch, navigate]);

    useEffect(() => { try { localStorage.setItem('rush.reports.tab', tab); } catch (e) { /* ignore */ } }, [tab]);

    const showToast = (msg) => { setToast(msg); window.clearTimeout(showToast._t); showToast._t = window.setTimeout(() => setToast(null), 2600); };

    const FILTERS = useMemo(() => {
        if (!D) return [];
        const projects = [...new Set((D.LQA_REVIEWS || []).map((r) => r[0]))];
        return [
            ['lang', 'Language', (D.LANGS || []).map((l) => l[1])],
            ['project', 'Project', projects],
        ];
    }, [D]);

    const toggleFilter = (id, opt) => setFilters((f) => ({ ...f, [id]: f[id].includes(opt) ? f[id].filter((x) => x !== opt) : [...f[id], opt] }));
    const clearFilter = (id) => setFilters((f) => ({ ...f, [id]: [] }));
    const clearAll = () => setFilters({ lang: [], project: [] });
    const activePills = FILTERS.flatMap(([id, label]) => filters[id].map((v) => ({ id, label, v })));

    const tabLabel = TABS.find((x) => x[0] === tab)[1];

    const doExport = (fmt) => {
        setMenu(null);
        if (fmt === 'CSV' && D) {
            const rows = [['Language', 'Words (k)', 'LQA', 'On-time %', 'Err/1k']]
                .concat((D.LANGS || []).map((l) => [l[1], l[2], l[3], l[4], l[5]]));
            const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `rush-reports-${tab}-${range}.csv`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
        showToast(`${tabLabel} report exported as ${fmt}`);
    };

    if (loading) return <div className="rpt-loading">Loading reports…</div>;
    if (denied) {
        return (
            <div className="rpt-page">
                <div className="rs-page-title-row"><h1 className="rs-page-title">Reports</h1></div>
                <div className="rpt-denied">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none"><rect x="4.5" y="10.5" width="15" height="9.5" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="M7.5 10.5V8a4.5 4.5 0 019 0v2.5" stroke="currentColor" strokeWidth="1.6" /></svg>
                    <h2>Reports are manager-only</h2>
                    <p>Workspace performance reports are available to managers and account owners. Ask your workspace manager if you need access.</p>
                </div>
            </div>
        );
    }
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!D) return null;

    const TabComp = { overview: OverviewTab, quality: QualityTab, productivity: ProductivityTab }[tab];

    return (
        <div className="rpt-page">
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Reports</h1>
                <div className="rs-page-actions">
                    <div className="rpt-range">
                        {D.RANGES.map(([id, label]) => (
                            <button type="button" key={id} className={range === id ? 'on' : ''} onClick={() => setRange(id)}>{label}</button>
                        ))}
                    </div>
                    <div className="rpt-facet-wrap">
                        <button type="button" className="btn-q" onClick={() => setMenu(menu === 'export' ? null : 'export')}>{I.download}<span>Export</span></button>
                        <Pop open={menu === 'export'} onClose={() => setMenu(null)} align="right" width={208}>
                            <div className="rpt-pop-h"><b>Export {tabLabel}</b></div>
                            <div className="rpt-pop-list">
                                <button type="button" className="rpt-exp-opt" onClick={() => doExport('PDF')}><span className="ic">{I.doc}</span><span><b>PDF</b><i>Formatted report</i></span></button>
                                <button type="button" className="rpt-exp-opt" onClick={() => doExport('CSV')}><span className="ic">{I.sheet}</span><span><b>CSV</b><i>Raw data tables</i></span></button>
                                <button type="button" className="rpt-exp-opt" onClick={() => doExport('PNG')}><span className="ic">{I.img}</span><span><b>PNG</b><i>Chart images</i></span></button>
                            </div>
                        </Pop>
                    </div>
                    <div className="rpt-facet-wrap">
                        <button type="button" className="btn-p" onClick={() => setMenu(menu === 'schedule' ? null : 'schedule')}>{I.cal}<span>Schedule</span></button>
                        <Pop open={menu === 'schedule'} onClose={() => setMenu(null)} align="right" width={288}>
                            <ScheduleForm tabLabel={tabLabel} onDone={(msg) => { setMenu(null); showToast(msg); }} />
                        </Pop>
                    </div>
                </div>
            </div>
            <p className="rs-page-sub">Localization performance across languages, projects, and your team — {D.RANGE_LABEL[range]}.</p>

            <div className="rpt-tabs">
                {TABS.map(([id, label]) => (
                    <button type="button" key={id} className={`rpt-tab ${tab === id ? 'on' : ''}`} onClick={() => setTab(id)}>{label}</button>
                ))}
            </div>

            <div className="rpt-toolbar">
                <span className="rpt-filter-ic">{I.filter}</span>
                {FILTERS.map(([id, label, options]) => (
                    <Facet key={id} id={id} label={label} options={options} selected={filters[id]}
                        onToggle={toggleFilter} onClear={clearFilter} open={menu} setOpen={setMenu} />
                ))}
                {activePills.length > 0 && <button type="button" className="rpt-clear-all" onClick={clearAll}>Clear all</button>}
            </div>

            {activePills.length > 0 && (
                <div className="rpt-pills">
                    {activePills.map((p) => (
                        <span className="rpt-pill" key={p.id + p.v}>
                            <i>{p.label}</i>{p.v}
                            <button type="button" onClick={() => toggleFilter(p.id, p.v)}>{I.x}</button>
                        </span>
                    ))}
                </div>
            )}

            <div className="rpt-tabbody" key={tab}>
                <TabComp D={D} range={range} filters={filters} />
            </div>

            {toast && <div className="rpt-toast">{I.check}<span>{toast}</span></div>}
        </div>
    );
};

export default Reports;
